import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ViewState } from '../states';
import { PathCard } from './PathCard';

/**
 * The path overview (spec §10.4), split into the two halves §9.4 asks for:
 * what was assigned and is therefore expected, and what is offered as
 * voluntary depth. The split is structural — two landmarked sections with
 * their own headings — not a colour or a sort order, so it survives a
 * screen-reader pass and a 200% zoom.
 */
function PathGroup({ titleKey, descriptionKey, paths, onOpen, emptyBody }) {
  const { t } = useTranslation();
  const headingId = useId();

  return (
    <section className="etch-path-group" aria-labelledby={headingId}>
      <h2 className="etch-path-group__title" id={headingId}>
        {t(titleKey)}
      </h2>
      <p className="etch-path-group__description">{t(descriptionKey)}</p>

      {paths.length === 0 ? (
        <p className="etch-path-group__empty">{emptyBody}</p>
      ) : (
        <ul className="etch-path-group__list">
          {paths.map((path) => (
            <li key={path.id}>
              <PathCard path={path} onOpen={onOpen} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function PathOverview({ paths, onOpen, generateAction, optionalEmptyBody }) {
  const { t } = useTranslation();

  const required = paths.filter((path) => path.required);
  const optional = paths.filter((path) => !path.required);

  if (paths.length === 0) {
    return (
      <ViewState
        status="empty"
        title={t('onboarding.emptyTitle')}
        description={t('onboarding.emptyBody')}
        action={generateAction}
      />
    );
  }

  return (
    <div className="etch-stack" data-gap="lg">
      <PathGroup
        titleKey="onboarding.requiredSection"
        descriptionKey="onboarding.requiredSectionDescription"
        paths={required}
        onOpen={onOpen}
        emptyBody={t('onboarding.emptyBody')}
      />
      <PathGroup
        titleKey="onboarding.optionalSection"
        descriptionKey="onboarding.optionalSectionDescription"
        paths={optional}
        onOpen={onOpen}
        emptyBody={optionalEmptyBody || t('onboarding.emptyBody')}
      />
    </div>
  );
}

export default PathOverview;
